import { useCallback, useEffect, useRef, useState } from 'react';

import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';

import type { ProductId } from '../../common/ui/app-tile/app-selector/types';
import {
	isRibbonAppTileDndData,
	isRibbonHideTileDndData,
} from '../../common/ui/drag-and-drop/utils';
import type { AppTileConfig } from '../../types';

/**
 * Sets up Ribbon drag-and-drop monitoring (mouse / pointer driven).
 *
 * - Tracks the currently dragging tile (to render the tile "disabled" state).
 * - Tracks whether a drag is active (to flip "More Apps" → "Hide").
 * - Applies in-memory reordering when dropping on another tile.
 * - Applies a "hide" operation when dropping on the Hide tile.
 *
 * Keyboard reordering is intentionally out of scope.
 */
export function useDragAndDrop({
	apps,
	onAppsReorder,
}: {
	apps: AppTileConfig[];
	onAppsReorder: (apps: AppTileConfig[]) => void;
}): {
	draggingProductId: ProductId | null;
	isDragActive: boolean;
} {
	const appsRef = useRef<AppTileConfig[]>(apps);
	useEffect(() => {
		// Keep the latest list in a ref so DnD callbacks always see current state,
		// without having to re-register the monitor on every reorder.
		appsRef.current = apps;
	}, [apps]);

	const updateApps = useCallback(
		(updater: (current: AppTileConfig[]) => AppTileConfig[]) => {
			// Update based on the latest list (not the list captured when callbacks were created).
			onAppsReorder(updater(appsRef.current));
		},
		[onAppsReorder],
	);

	const [draggingProductId, setDraggingProductId] = useState<ProductId | null>(null);
	const [isDragActive, setIsDragActive] = useState(false);

	useEffect(() => {
		return monitorForElements({
			// Only respond to drags started from ribbon app tiles.
			canMonitor: ({ source }) => isRibbonAppTileDndData(source.data),
			onDragStart({ source }) {
				if (!isRibbonAppTileDndData(source.data)) {
					return;
				}
				setIsDragActive(true);
				setDraggingProductId(source.data.productId);
			},
			onDrop({ source, location }) {
				setIsDragActive(false);
				setDraggingProductId(null);

				const sourceData = source.data;
				if (!isRibbonAppTileDndData(sourceData)) {
					return;
				}

				// Pragmatic DnD provides dropTargets ordered from inner-most → outer-most.
				// We only care about the inner-most actionable target for reordering/hiding.
				const [innerMost] = location.current.dropTargets;
				if (!innerMost) {
					return;
				}

				const dropTargetData = innerMost.data;
				const startIndex = sourceData.index;

				// Drop onto Hide tile moves it to the end
				if (isRibbonHideTileDndData(dropTargetData)) {
					updateApps((current) =>
						reorder({ list: current, startIndex, finishIndex: current.length - 1 }),
					);
					return;
				}

				// Drop onto another tile reorders
				if (!isRibbonAppTileDndData(dropTargetData)) {
					return;
				}

				// The reorder target includes a "closest edge" (top/bottom) instruction attached by the
				// hovered drop target (see `ReorderableAppTile.getData`).
				const closestEdgeOfTarget = extractClosestEdge(dropTargetData);
				const finishIndex = getReorderDestinationIndex({
					startIndex,
					closestEdgeOfTarget,
					indexOfTarget: dropTargetData.index,
					axis: 'vertical',
				});

				// If dropping back to the same position, skip any state updates.
				if (finishIndex === startIndex) {
					return;
				}

				updateApps((current) => reorder({ list: current, startIndex, finishIndex }));
			},
		});
	}, [updateApps]);

	return { draggingProductId, isDragActive };
}
